import { useDebounce } from "primereact/hooks";
import { useEffect, useMemo } from "react";
import { useWatch, Control } from "react-hook-form";

type Params = {
  control: Control<any>;
  triggerFieldKeys: string[];
  debounceMs?: number;
};

export const useDependentTriggerWatch = ({
  control,
  triggerFieldKeys,
  debounceMs = 300
}: Params) => {
  // RHF v6's useWatch returns an OBJECT keyed by field name when `name` is an
  // array (NOT an array), and a fresh reference on every render even when the
  // values are unchanged. Read each watched value by its key, not by index.
  const watchedValues = useWatch({
    control,
    name: triggerFieldKeys
  }) as Record<string, unknown> | undefined;

  // Derive a content-based signature so the memoized value map only changes on
  // real value changes — otherwise the fresh object reference would re-fire the
  // debounced setter every render.
  const valueMapSignature = triggerFieldKeys
    .map(key => `${key}=${JSON.stringify(watchedValues?.[key] ?? null)}`)
    .join("|");

  const valueMap = useMemo<Record<string, unknown>>(() => {
    const result: Record<string, unknown> = {};
    triggerFieldKeys.forEach(key => {
      result[key] = watchedValues?.[key];
    });
    return result;
    // Stabilized via valueMapSignature; raw watchedValues ref churn is expected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMapSignature]);

  const [, debouncedValues, setDebouncedValues] = useDebounce<
    Record<string, unknown>
  >(valueMap, debounceMs);

  useEffect(() => {
    setDebouncedValues(valueMap);
    // setDebouncedValues from useDebounce is stable; depend only on the value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMap]);

  return { debouncedValues, instantValues: valueMap };
};
